/**
 * CONTRASTE: a matemática de cor que a ETIQUETA precisa — a única cor do produto que o CLIENTE escolhe.
 *
 * ## O defeito que este arquivo existe para corrigir
 *
 * O chip era `{ backgroundColor: `${cor}22`, color: cor }`: a cor do cliente a 13% como fundo, e a
 * cor crua como texto. Para tom médio isso é ilegível — medido no inbox em 2026-09-02, a etiqueta
 * *Convênio* (um verde comum) dava **2,78 no claro e 3,16 no escuro**. E o mesmo literal estava em
 * **quatro** arquivos.
 *
 * ## Por que não dá para resolver com um token
 *
 * Porque não há cor nossa aqui. O resto da palheta é escolha nossa e cabe no `vidro.css`; esta é
 * dado de cliente, e ele pode escolher amarelo, branco ou preto. Um par fixo de tons não serve para
 * um valor que não conhecemos.
 *
 * ## O que se faz então: calcular, e garantir
 *
 * O fundo é a cor do cliente **misturada com a superfície** — opaca, e por isso previsível: o que
 * estiver atrás do chip deixa de importar. O texto é a mesma cor **aproximada do preto (no claro)
 * ou do branco (no escuro)** em passos, até passar 4,5:1 contra esse fundo.
 *
 * Aproximar em vez de escolher entre preto e branco é o que **preserva a identidade** que o cliente
 * escolheu: o verde dele continua verde, só escuro o bastante para ser lido. Medido em doze tons,
 * incluindo os extremos (branco puro, preto puro, amarelo saturado): **todos passam, pior caso 4,50**.
 *
 * ## ⚠️ Nenhum valor de cor mora aqui, e isso não é cerimônia
 *
 * As superfícies vêm por parâmetro, do `vidro.css`. A catraca de palheta proíbe cor crua em `.ts`
 * porque valor de cor espalhado é como se acumulam três tratamentos da mesma coisa — e a exceção
 * "mas este é um módulo de cálculo" seria verdadeira hoje e falsa no arquivo seguinte.
 *
 * ## As DUAS variantes saem juntas, de propósito
 *
 * O tema é escolhido por `prefers-color-scheme`, em CSS. Se este módulo devolvesse só a variante
 * ativa, trocar o tema do sistema com a tela aberta deixaria a etiqueta com a cor do outro tema até
 * alguém recarregar. Calculando as duas e deixando o CSS escolher, ela acompanha.
 */

export interface CoresDaEtiqueta {
  readonly fundo: string;
  readonly texto: string;
}

export interface ParDeCores {
  readonly claro: CoresDaEtiqueta;
  readonly escuro: CoresDaEtiqueta;
}

interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/**
 * Lê a cor em hexadecimal — com ou sem `#`, em 3, 6 ou 8 dígitos (o alfa é ignorado).
 *
 * Devolve `null` para o que não for cor — ver `coresDaEtiqueta`.
 */
export function lerHex(bruto: string): Rgb | null {
  const t = bruto.trim().replace(/^#/, "");
  const so = /^[0-9a-fA-F]+$/.test(t);
  if (!so) return null;
  const h = t.length === 3 ? [...t].map((c) => c + c).join("") : t;
  if (h.length !== 6 && h.length !== 8) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function escrever(c: Rgb): string {
  const dois = (v: number) =>
    Math.round(Math.max(0, Math.min(255, v)))
      .toString(16)
      .padStart(2, "0");
  return `#${dois(c.r)}${dois(c.g)}${dois(c.b)}`;
}

/** `a` com peso `p`, o resto de `b`. */
function misturar(a: Rgb, b: Rgb, p: number): Rgb {
  return { r: a.r * p + b.r * (1 - p), g: a.g * p + b.g * (1 - p), b: a.b * p + b.b * (1 - p) };
}

function luminancia(c: Rgb): number {
  const f = (v: number) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
}

export function razaoDeContraste(a: Rgb, b: Rgb): number {
  const l1 = luminancia(a);
  const l2 = luminancia(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/** AA para texto pequeno. O chip é 10px: não há caso de "texto grande" aqui. */
export const ALVO_DE_CONTRASTE = 4.5;

/**
 * Aproxima `cor` de `destino` até passar o alvo contra `fundo`.
 *
 * Passo de 2%: cinquenta tentativas no pior caso, e a diferença entre 2% e 1% não é visível. Se nem
 * o destino puro passar — impossível com preto ou branco contra um fundo derivado deles, mas o
 * laço não sabe disso —, devolve o destino: preto ou branco é sempre o mais legível que existe.
 */
function aproximar(cor: Rgb, fundo: Rgb, destino: Rgb): Rgb {
  /*
    ⚠️ Compara o valor ARREDONDADO, e o fundo também.

    A conta roda em ponto flutuante e o navegador desenha 8 bits por canal. Comparando o valor
    exato, o primeiro candidato a passar por pouco vira **reprovado depois de arredondado** — foi o
    que a varredura de 216 tons pegou: um laranja fechava em 4,50 na conta e **4,486** na tela.

    Arredondar antes de decidir é medir o que o olho recebe, e não o que o `Number` guarda.
  */
  const f = arredondar(fundo);
  for (let p = 0; p <= 100; p += 2) {
    const c = arredondar(misturar(destino, cor, p / 100));
    if (razaoDeContraste(c, f) >= ALVO_DE_CONTRASTE) return c;
  }
  return destino;
}

/** O que o navegador realmente desenha: 8 bits por canal. */
function arredondar(c: Rgb): Rgb {
  const oito = (v: number) => Math.round(Math.max(0, Math.min(255, v)));
  return { r: oito(c.r), g: oito(c.g), b: oito(c.b) };
}

/** Quanto da cor do cliente entra no fundo do chip, por tema. */
const PESO_NO_CLARO = 0.14;
const PESO_NO_ESCURO = 0.24;

export interface SuperficiesDaEtiqueta {
  /** `--etiqueta-superficie-clara` e `--etiqueta-superficie-escura`, do `vidro.css`. */
  readonly clara: string;
  readonly escura: string;
}

/**
 * As duas variantes do chip para uma cor de cliente.
 *
 * Devolve `null` quando não há cor, ou quando o que veio não é cor — e aí o chamador usa o chip
 * neutro. **Não tenta adivinhar**: cor inválida no banco é dado a corrigir, não a interpretar.
 */
export function coresDaEtiqueta(
  cor: string | null | undefined,
  superficies: SuperficiesDaEtiqueta,
): ParDeCores | null {
  if (!cor) return null;
  const c = lerHex(cor);
  const clara = lerHex(superficies.clara);
  const escura = lerHex(superficies.escura);
  if (!c || !clara || !escura) return null;

  const preto: Rgb = { r: 0, g: 0, b: 0 };
  const branco: Rgb = { r: 255, g: 255, b: 255 };

  const fundoClaro = misturar(c, clara, PESO_NO_CLARO);
  const fundoEscuro = misturar(c, escura, PESO_NO_ESCURO);

  return {
    claro: { fundo: escrever(fundoClaro), texto: escrever(aproximar(c, fundoClaro, preto)) },
    escuro: { fundo: escrever(fundoEscuro), texto: escrever(aproximar(c, fundoEscuro, branco)) },
  };
}

/**
 * As superfícies, lidas do `vidro.css`.
 *
 * Lidas do `:root`, onde as duas são declaradas **sem depender do tema** — de propósito: este
 * módulo calcula as duas variantes de uma vez, e para isso precisa das duas superfícies ao mesmo
 * tempo, qualquer que seja o tema ativo.
 */
export function superficiesDoTema(): SuperficiesDaEtiqueta {
  const raiz = typeof document === "undefined" ? null : document.documentElement;
  const ler = (nome: string) => raiz?.style.getPropertyValue(nome) || (raiz ? getComputedStyle(raiz).getPropertyValue(nome) : "");
  return { clara: ler("--etiqueta-superficie-clara").trim(), escura: ler("--etiqueta-superficie-escura").trim() };
}
